import type {
  AdminOverviewDto,
  AuthResponse,
  AttachmentDto,
  ChatDetailsDto,
  ChatEventDto,
  ChatMemberRole,
  ChatSummaryDto,
  MessageDto,
  MessageContextDto,
  MessageSearchPageDto,
  MessageSearchQuery,
  NotificationSettingsDto,
  ChatNotificationSettingsDto,
  PushSubscriptionInput,
  PublicUser,
  SessionInfo,
  StorageStatusDto,
  UserRole,
} from '@private-messenger/shared';

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : undefined;
}

export class ApiClientError extends Error {
  public constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    const csrf = readCookie('pm_csrf');
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const response = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiClientError(
      body?.error?.message ?? 'Не удалось выполнить запрос',
      body?.error?.code ?? 'REQUEST_FAILED',
      response.status,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiClientError(
      body?.error?.message ?? 'Не удалось открыть файл',
      body?.error?.code ?? 'REQUEST_FAILED',
      response.status,
    );
  }
  return response.blob();
}

export const api = {
  session: () => request<AuthResponse>('/auth/session'),
  login: (login: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  logoutAll: () => request<void>('/auth/logout-all', { method: 'POST' }),
  sessions: () => request<{ sessions: SessionInfo[] }>('/auth/sessions'),
  revokeSession: (id: string) => request<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<AuthResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  updateProfile: (displayName: string) =>
    request<AuthResponse>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),
  users: () => request<{ users: PublicUser[] }>('/users'),
  chats: () => request<{ chats: ChatSummaryDto[] }>('/chats'),
  chat: (id: string) => request<{ chat: ChatDetailsDto }>(`/chats/${id}`),
  setChatPinned: (chatId: string, pinned: boolean) =>
    request<{ chat: ChatSummaryDto }>(`/chats/${chatId}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ pinned }),
    }),
  chatEvents: (after: string) =>
    request<{ events: ChatEventDto[] }>(`/chats/events?after=${after}&limit=500`),
  messages: (chatId: string, before?: string) =>
    request<{ messages: MessageDto[]; nextCursor: string | null }>(
      `/chats/${chatId}/messages?limit=50${before ? `&before=${before}` : ''}`,
    ),
  message: (messageId: string) => request<{ message: MessageDto }>(`/messages/${messageId}`),
  messageContext: (chatId: string, messageId: string) =>
    request<{ context: MessageContextDto }>(`/chats/${chatId}/messages/${messageId}/context`),
  searchMessages: (query: MessageSearchQuery) =>
    request<MessageSearchPageDto>(`/messages/search?${searchParams(query).toString()}`),
  createDirect: (userId: string) =>
    request<{ chat: ChatDetailsDto }>('/chats/direct', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  createGroup: (input: { title: string; description: string; memberIds: string[] }) =>
    request<{ chat: ChatDetailsDto }>('/chats/groups', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateGroup: (chatId: string, input: { title?: string; description?: string }) =>
    request<{ chat: ChatDetailsDto }>(`/chats/${chatId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  addMember: (chatId: string, userId: string, role: Exclude<ChatMemberRole, 'OWNER'>) =>
    request<{ chat: ChatDetailsDto }>(`/chats/${chatId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),
  updateMemberRole: (chatId: string, userId: string, role: Exclude<ChatMemberRole, 'OWNER'>) =>
    request<{ chat: ChatDetailsDto }>(`/chats/${chatId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeMember: (chatId: string, userId: string) =>
    request<{ chat: ChatDetailsDto }>(`/chats/${chatId}/members/${userId}`, {
      method: 'DELETE',
    }),
  leaveGroup: (chatId: string) => request<void>(`/chats/${chatId}/leave`, { method: 'POST' }),
  transferOwner: (chatId: string, userId: string) =>
    request<{ chat: ChatDetailsDto }>(`/chats/${chatId}/transfer-owner`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  sendMessage: (
    chatId: string,
    input: {
      clientId: string;
      text: string;
      replyToId?: string;
      replyToAttachmentId?: string;
      attachmentIds?: string[];
    },
  ) =>
    request<{ message: MessageDto }>(`/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createAttachment: (
    chatId: string,
    input: { originalName: string; mimeType: string; sizeBytes: number; caption?: string },
  ) =>
    request<{ attachment: AttachmentDto }>(`/chats/${chatId}/attachments`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  uploadAttachment: (attachmentId: string, file: File, onProgress: (progress: number) => void) =>
    uploadAttachment(attachmentId, file, onProgress),
  attachmentDownloadUrl: (attachmentId: string) => `/api/v1/attachments/${attachmentId}/download`,
  attachmentViewBlob: (attachmentId: string, signal?: AbortSignal) =>
    requestBlob(`/attachments/${attachmentId}/view`, signal),
  attachmentPreviewUrl: (attachmentId: string) => `/api/v1/attachments/${attachmentId}/preview`,
  attachmentBlob: (attachmentId: string, signal?: AbortSignal) =>
    requestBlob(`/attachments/${attachmentId}/download`, signal),
  discardAttachment: (attachmentId: string) =>
    request<void>(`/attachments/${attachmentId}`, { method: 'DELETE' }),
  editMessage: (messageId: string, text: string) =>
    request<{ message: MessageDto }>(`/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),
  deleteMessage: (messageId: string) =>
    request<void>(`/messages/${messageId}`, { method: 'DELETE' }),
  toggleReaction: (messageId: string, emoji: string) =>
    request<{ message: MessageDto }>(`/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),
  markRead: (chatId: string, messageId: string) =>
    request<void>(`/chats/${chatId}/read`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),
  adminUsers: () => request<{ users: PublicUser[] }>('/admin/users'),
  adminOverview: () => request<{ overview: AdminOverviewDto }>('/admin/overview'),
  adminStorage: () => request<{ storage: StorageStatusDto }>('/admin/storage'),
  createUser: (input: { login: string; displayName: string; password: string; role: UserRole }) =>
    request<{ user: PublicUser }>('/admin/users', { method: 'POST', body: JSON.stringify(input) }),
  setBlocked: (id: string, blocked: boolean) =>
    request<{ user: PublicUser }>(`/admin/users/${id}/${blocked ? 'block' : 'unblock'}`, {
      method: 'PATCH',
    }),
  resetPassword: (id: string, password: string) =>
    request<void>(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  notificationSettings: () =>
    request<{ settings: NotificationSettingsDto }>('/notifications/settings'),
  updateNotificationSettings: (enabled: boolean) =>
    request<{ settings: NotificationSettingsDto }>('/notifications/settings', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  subscribePush: (subscription: PushSubscriptionInput) =>
    request<void>('/notifications/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),
  unsubscribePush: (endpoint: string) =>
    request<void>('/notifications/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  chatNotificationSettings: (chatId: string) =>
    request<{ settings: ChatNotificationSettingsDto }>(`/chats/${chatId}/notifications`),
  updateChatNotificationSettings: (chatId: string, muted: boolean) =>
    request<{ settings: ChatNotificationSettingsDto }>(`/chats/${chatId}/notifications`, {
      method: 'PATCH',
      body: JSON.stringify({ muted }),
    }),
};

function searchParams(query: MessageSearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value));
  });
  return params;
}

function uploadAttachment(
  attachmentId: string,
  file: File,
  onProgress: (progress: number) => void,
): Promise<{ attachment: AttachmentDto }> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', `/api/v1/attachments/${attachmentId}/content`);
    request.withCredentials = true;
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    const csrf = readCookie('pm_csrf');
    if (csrf) request.setRequestHeader('X-CSRF-Token', csrf);
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener('load', () => {
      const body = parseJson(request.responseText);
      if (request.status >= 200 && request.status < 300 && body && 'attachment' in body) {
        onProgress(100);
        resolve(body);
        return;
      }
      const error = body && 'error' in body ? body.error : undefined;
      reject(
        new ApiClientError(
          error?.message ?? 'Не удалось загрузить файл',
          error?.code ?? 'UPLOAD_FAILED',
          request.status,
        ),
      );
    });
    request.addEventListener('error', () => {
      reject(new ApiClientError('Соединение прервано при загрузке', 'UPLOAD_NETWORK', 0));
    });
    request.send(file);
  });
}

function parseJson(
  value: string,
): { attachment: AttachmentDto } | { error: { code?: string; message?: string } } | null {
  try {
    return JSON.parse(value) as
      { attachment: AttachmentDto } | { error: { code?: string; message?: string } };
  } catch {
    return null;
  }
}
